/**
 * Unified User Data Context
 *
 * Combines PlayerShortlistContext and FixtureNotesContext into a single provider
 * for user-specific annotations and preferences.
 *
 * This context handles:
 * - Player shortlist (favorite players to track)
 * - Fixture notes (personal notes on matches)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  getPlayerShortlist,
  addToPlayerShortlist,
  removePlayerFromShortlist,
  getFixtureNotes,
  saveFixtureNote,
  deleteFixtureNote,
  waitForAuthReady,
  type PlayerShortlistItem,
  type FixtureNoteAPI,
} from '../api'

// ============================================
// CONTEXT TYPE
// ============================================

interface UserDataContextType {
  // Player Shortlist
  shortlist: PlayerShortlistItem[]
  isLoadingShortlist: boolean
  isShortlisted: (playerId: number) => boolean
  toggleShortlist: (playerId: number, playerName?: string) => Promise<void>
  refreshShortlist: () => Promise<void>

  // Fixture Notes
  notes: Map<number, string>
  isLoadingNotes: boolean
  getNote: (fixtureId: number) => string | undefined
  saveNote: (fixtureId: number, content: string) => Promise<void>
  deleteNote: (fixtureId: number) => Promise<void>
  refreshNotes: () => Promise<void>
}

const UserDataContext = createContext<UserDataContextType | null>(null)

// ============================================
// HELPERS
// ============================================

const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

// ============================================
// PROVIDER
// ============================================

export function UserDataProvider({ children }: { children: React.ReactNode }) {
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false, isLoaded: true }
  const { isSignedIn, isLoaded } = auth

  // ---- Shortlist State ----
  const [shortlist, setShortlist] = useState<PlayerShortlistItem[]>([])
  const [isLoadingShortlist, setIsLoadingShortlist] = useState(false)

  // ---- Notes State ----
  const [notes, setNotes] = useState<Map<number, string>>(new Map())
  const [isLoadingNotes, setIsLoadingNotes] = useState(false)

  // ---- Fetch data on sign in ----
  useEffect(() => {
    if (!clerkEnabled || !isLoaded || !isSignedIn) {
      setShortlist([])
      setNotes(new Map())
      return
    }

    const fetchAll = async () => {
      await waitForAuthReady()

      // Fetch shortlist
      setIsLoadingShortlist(true)
      try {
        const items = await getPlayerShortlist()
        setShortlist(items)
      } catch (err) {
        console.error('Failed to fetch player shortlist:', err)
      } finally {
        setIsLoadingShortlist(false)
      }

      // Fetch notes
      setIsLoadingNotes(true)
      try {
        const noteItems = await getFixtureNotes()
        const notesMap = new Map<number, string>()
        noteItems.forEach((item) => {
          notesMap.set(item.fixture_id, item.content)
        })
        setNotes(notesMap)
      } catch (err) {
        console.error('Failed to fetch fixture notes:', err)
      } finally {
        setIsLoadingNotes(false)
      }
    }

    let cancelled = false
    fetchAll().then(() => {
      if (cancelled) return
    })
    return () => {
      cancelled = true
    }
  }, [isSignedIn, isLoaded])

  // ---- Shortlist Methods ----
  const isShortlisted = useCallback(
    (playerId: number) => shortlist.some((item) => item.player_id === playerId),
    [shortlist]
  )

  const toggleShortlist = useCallback(
    async (playerId: number, playerName?: string) => {
      if (!clerkEnabled || !isSignedIn) return

      const existing = shortlist.find((item) => item.player_id === playerId)

      if (existing) {
        try {
          await removePlayerFromShortlist(playerId)
          setShortlist((prev) => prev.filter((item) => item.player_id !== playerId))
        } catch (err) {
          console.error('Failed to remove from shortlist:', err)
          throw err
        }
      } else {
        try {
          const created = await addToPlayerShortlist(playerId)
          setShortlist((prev) => [...prev, created])
        } catch (err) {
          console.error('Failed to add to shortlist:', err)
          throw err
        }
      }
    },
    [isSignedIn, shortlist]
  )

  const refreshShortlist = useCallback(async () => {
    if (!clerkEnabled || !isSignedIn) return

    setIsLoadingShortlist(true)
    try {
      const items = await getPlayerShortlist()
      setShortlist(items)
    } catch (err) {
      console.error('Failed to refresh shortlist:', err)
    } finally {
      setIsLoadingShortlist(false)
    }
  }, [isSignedIn])

  // ---- Notes Methods ----
  const getNote = useCallback((fixtureId: number) => notes.get(fixtureId), [notes])

  const saveNoteFn = useCallback(
    async (fixtureId: number, content: string) => {
      if (!clerkEnabled || !isSignedIn) return

      try {
        await saveFixtureNote(fixtureId, content)
        setNotes((prev) => {
          const newMap = new Map(prev)
          newMap.set(fixtureId, content)
          return newMap
        })
      } catch (err) {
        console.error('Failed to save fixture note:', err)
        throw err
      }
    },
    [isSignedIn]
  )

  const deleteNoteFn = useCallback(
    async (fixtureId: number) => {
      if (!clerkEnabled || !isSignedIn) return

      try {
        await deleteFixtureNote(fixtureId)
        setNotes((prev) => {
          const newMap = new Map(prev)
          newMap.delete(fixtureId)
          return newMap
        })
      } catch (err) {
        console.error('Failed to delete fixture note:', err)
        throw err
      }
    },
    [isSignedIn]
  )

  const refreshNotes = useCallback(async () => {
    if (!clerkEnabled || !isSignedIn) return

    setIsLoadingNotes(true)
    try {
      const noteItems = await getFixtureNotes()
      const notesMap = new Map<number, string>()
      noteItems.forEach((item) => {
        notesMap.set(item.fixture_id, item.content)
      })
      setNotes(notesMap)
    } catch (err) {
      console.error('Failed to refresh fixture notes:', err)
    } finally {
      setIsLoadingNotes(false)
    }
  }, [isSignedIn])

  // ---- Memoized value ----
  const value = useMemo(
    () => ({
      // Shortlist
      shortlist,
      isLoadingShortlist,
      isShortlisted,
      toggleShortlist,
      refreshShortlist,
      // Notes
      notes,
      isLoadingNotes,
      getNote,
      saveNote: saveNoteFn,
      deleteNote: deleteNoteFn,
      refreshNotes,
    }),
    [
      shortlist,
      isLoadingShortlist,
      isShortlisted,
      toggleShortlist,
      refreshShortlist,
      notes,
      isLoadingNotes,
      getNote,
      saveNoteFn,
      deleteNoteFn,
      refreshNotes,
    ]
  )

  return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>
}

// ============================================
// HOOKS
// ============================================

export function useUserData() {
  const ctx = useContext(UserDataContext)
  if (!ctx) throw new Error('useUserData must be used within UserDataProvider')
  return ctx
}

// Convenience hooks for backward compatibility
export function usePlayerShortlist() {
  const ctx = useUserData()
  return {
    shortlist: ctx.shortlist,
    isLoading: ctx.isLoadingShortlist,
    isShortlisted: ctx.isShortlisted,
    toggleShortlist: ctx.toggleShortlist,
    refreshShortlist: ctx.refreshShortlist,
  }
}

export function useFixtureNotes() {
  const ctx = useUserData()
  return {
    notes: ctx.notes,
    isLoading: ctx.isLoadingNotes,
    getNote: ctx.getNote,
    saveNote: ctx.saveNote,
    deleteNote: ctx.deleteNote,
    refreshNotes: ctx.refreshNotes,
  }
}
